import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  apiService,
  AffiliateMember,
  AffiliateMemberPayload,
  User,
  AffiliateMemberStatus,
  PaymentMethod,
} from '../services/api';
import ConfirmModal from './ConfirmModal';

interface AffiliateMemberEditModalProps {
  visible: boolean;
  member: AffiliateMember | null;
  onCancel: () => void;
  onSuccess: () => void;
}

interface FormState {
  userId: string;
  walletBalance: string;
  paymentMethodId: number | '';
  accountNumber: string;
  status: AffiliateMemberStatus;
}

const AffiliateMemberEditModal: React.FC<AffiliateMemberEditModalProps> = ({
  visible,
  member,
  onCancel,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormState>({
    userId: '',
    walletBalance: '0',
    paymentMethodId: '',
    accountNumber: '',
    status: AffiliateMemberStatus.ACTIVE,
  });

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isUpdateConfirmVisible, setIsUpdateConfirmVisible] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  useEffect(() => {
    if (!visible || !member) return;

    fetchUsers();
    fetchPaymentMethods();

    setFormData({
      userId: member.userId?.toString() ?? '',
      walletBalance: member.walletBalance.toString(),
      paymentMethodId: member.paymentMethodId ?? '',
      accountNumber: member.accountNumber ?? '',
      status: member.status,
    });

    if (member.user) {
      setUserSearchTerm(member.user.name || member.user.email || '');
    }
  }, [visible, member]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiService.getUsers({ limit: 100 });
      setUsers(response.users ?? []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await apiService.getPaymentMethods();
      setPaymentMethods(response);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!userSearchTerm) return true;
    const q = userSearchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(q) ||
      user.name?.toLowerCase().includes(q) ||
      user.username?.toLowerCase().includes(q)
    );
  });

  const selectedUser = users.find(u => u.id === Number(formData.userId));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;

    const hasChanges =
      Number(formData.userId || 0) !== member.userId ||
      Number(formData.walletBalance) !== member.walletBalance ||
      formData.status !== member.status ||
      formData.paymentMethodId !== (member.paymentMethodId ?? '') ||
      formData.accountNumber !== (member.accountNumber ?? '');

    if (!hasChanges) {
      onSuccess();
      return;
    }

    setIsUpdateConfirmVisible(true);
  };

  const confirmUpdate = async () => {
    if (!member) return;

    try {
      setLoading(true);

      const payload: Partial<AffiliateMemberPayload> = {
        userId: formData.userId ? Number(formData.userId) : undefined,
        walletBalance: Number(formData.walletBalance),
        status: formData.status,
        paymentMethodId:Number(formData.paymentMethodId),
        accountNumber: formData.accountNumber,
      };

      await apiService.updateAffiliateMember(member.id, payload);
      setIsUpdateConfirmVisible(false);
      onSuccess();
    } catch (error: any) {
      console.error('Failed to update affiliate member:', error);
      alert(error.message || 'Failed to update affiliate member');
    } finally {
      setLoading(false);
    }
  };

  if (!visible || !member) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-bold">Edit Affiliate Member</h2>
          <button onClick={onCancel}>
            <XMarkIcon className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {/* USER */}
          <div>
            <label className="text-sm font-medium">User</label>
            <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" /> <input type="text" placeholder="Search user by email or name..." value={userSearchTerm} onChange={(e) => { setUserSearchTerm(e.target.value); setShowUserDropdown(true); if (!e.target.value) { setFormData(prev => ({ ...prev, userId: '' })); } }} onFocus={() => setShowUserDropdown(true)} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary" disabled={loadingUsers} />

              {showUserDropdown && (
                <div className="absolute z-20 bg-white border rounded-md w-full mt-1 max-h-48 overflow-y-auto">
                  {filteredUsers.map(user => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          userId: user.id.toString(),
                        }));
                        setUserSearchTerm(user.name || user.email || '');
                        setShowUserDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      <div className="text-sm font-medium">
                        {user.name || 'No name'}
                      </div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedUser && (
                <div className="mt-2 p-2 bg-gray-50 rounded-md">
                  <div className="text-sm font-medium text-gray-900">
                    {selectedUser.name || 'No name'}
                  </div>
                  <div className="text-xs text-gray-500">{selectedUser.email}</div>
                </div>
              )}
          </div>

          {/* WALLET */}
          <div>
            <label className="text-sm font-medium">Wallet Balance</label>
            <input
              type="number"
              value={formData.walletBalance}
              onChange={e =>
                setFormData(prev => ({ ...prev, walletBalance: e.target.value }))
              }
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          {/* PAYMENT METHOD */}
          <div>
            <label className="text-sm font-medium">Payment Method</label>
            <select
              value={formData.paymentMethodId}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  paymentMethodId: e.target.value ? Number(e.target.value) : '',
                }))
              }
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Select Payment Method</option>
              {paymentMethods.map(pm => (
                <option key={pm.id} value={pm.id}>
                  {pm.name}
                </option>
              ))}
            </select>
          </div>

          {/* ACCOUNT NUMBER */}
          <div>
            <label className="text-sm font-medium">Account Number</label>
            <input
              value={formData.accountNumber}
              onChange={e =>
                setFormData(prev => ({ ...prev, accountNumber: e.target.value }))
              }
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          {/* STATUS */}
          <div>
            <label className="text-sm font-medium">Status</label>
            <select
              value={formData.status}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  status: e.target.value as AffiliateMemberStatus,
                }))
              }
              className="w-full border rounded-md px-3 py-2"
            >
              <option value={AffiliateMemberStatus.ACTIVE}>Active</option>
              <option value={AffiliateMemberStatus.INACTIVE}>Inactive</option>
              <option value={AffiliateMemberStatus.BANNED}>Banned</option>
            </select>
          </div>

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onCancel} className="btn-outline">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>

        <ConfirmModal
          visible={isUpdateConfirmVisible}
          title="Update Affiliate Member"
          message="Are you sure you want to update this affiliate member?"
          confirmText="Update"
          cancelText="Cancel"
          onConfirm={confirmUpdate}
          onCancel={() => setIsUpdateConfirmVisible(false)}
        />
      </div>
    </div>
  );
};

export default AffiliateMemberEditModal;
